import React, { useState, useEffect, useContext, useMemo, useRef } from 'react';
import {
      CButton,
      CModal,
      CModalHeader,
      CModalBody,
      CModalFooter,
      CCard,
      CModalTitle,
} from '@coreui/react';
import { CChartPie, CChartDoughnut, CChartBar, CChartLine } from '@coreui/react-chartjs';
import { pieChartOptions, doughnutChartOptions, barChartOptions, lineChartOptions } from '../../utils/chartTheme';
import axios from 'axios';
import { AuthUserContext } from '../session/context';
import config from '../../utils/EnvironmentSwitcher';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { getDatabase, ref, get } from 'firebase/database';
import { buildFhirHeaders } from '../../utils/fhirAuth';
import { callAI } from '../../services/aiChatService';

import { getFirstAndLastDayOfMonth } from '../cards/RhythmCard/RhythmUtils';

// Browser-safe FHIR proxy endpoint (Node BFF -> FHIR upstream)
const fhirEndpoint = `${String(config.apiUrl || '').replace(/\/+$/, '')}/api/fhir`;
// fhirSystemBase is canonical — FHIR identifier system URIs are permanent labels baked into
// stored resources and do NOT change between dev/prod environments.
const fhirIdSystem = `${String(config.fhirSystemBase || '').replace(/\/+$/, '')}/fhir/ids`;

const getFhirProxyHeaders = async (forceRefresh = false) => {
      return buildFhirHeaders({ 'Content-Type': 'application/fhir+json' }, forceRefresh);
};

const axiosGetWithFhirRetry = async (url) => {
      const headers = await getFhirProxyHeaders(false);
      if (!headers.Authorization) {
            throw new Error('Missing Firebase ID token for /api/fhir proxy');
      }
      try {
            return await axios.get(url, { headers });
      } catch (error) {
            if (error?.response?.status === 401) {
                  const refreshedHeaders = await getFhirProxyHeaders(true);
                  if (!refreshedHeaders.Authorization) {
                        throw new Error('Unable to refresh Firebase ID token for /api/fhir proxy');
                  }
                  return axios.get(url, { headers: refreshedHeaders });
            }
            throw error;
      }
};

// All AI prompts go to the mieGPT endpoint (chat.mieros.cloud/gptj). See src/services/aiChatService.js.
const callOpenAI = async (prompt, systemMessage = 'You are MiEROS, a friendly AI health companion. Analyze the user\'s health observations and provide personalized, actionable insights. Be encouraging but honest. Use their actual data numbers in your response. Keep responses concise (2-3 short paragraphs). End with a practical tip. Always remind users to consult healthcare professionals for medical decisions.') =>
      callAI({ prompt, systemMessage });

// LOINC codes and display names for common vital signs
// Maps LOINC code to possible display names in FHIR observations
const METRIC_CONFIG = {
      HEART_RATE: { code: '8867-4', names: ['Heart Rate', 'heart rate', 'HR'] },
      BODY_WEIGHT: { code: '29463-7', names: ['Weight', 'weight', 'Body Weight'] },
      BODY_TEMPERATURE: { code: '8310-5', names: ['Body Temperature', 'Temperature', 'temperature', 'Temp'] },
      OXYGEN_SATURATION: { code: '59408-5', names: ['Oxygen Saturation', 'SpO2', 'O2 Sat', 'oxygen saturation'] },
      BLOOD_GLUCOSE: { code: '2339-0', names: ['Blood Glucose', 'Glucose', 'blood glucose'] },
      STEPS: { code: '55423-8', names: ['Steps Taken', 'Steps', 'steps', 'Step Count'] },
      SLEEP_DURATION: { code: '93832-4', names: ['Sleep Hours', 'Sleep', 'sleep', 'Sleep Duration'] },
      SYSTOLIC_BP: { code: '8480-6', names: ['Blood Pressure Systolic', 'Systolic', 'systolic', 'SBP'] },
      DIASTOLIC_BP: { code: '8462-4', names: ['Blood Pressure Diastolic', 'Diastolic', 'diastolic', 'DBP'] },
      CALORIES: { code: '41981-2', names: ['Calories', 'calories', 'Caloric Intake', 'Energy'] },
      WATER_INTAKE: { code: '77255-5', names: ['Water Intake', 'water intake', 'Hydration', 'Water'] },
};

// Legacy LOINC_CODES object for backward compatibility
const LOINC_CODES = {
      HEART_RATE: METRIC_CONFIG.HEART_RATE.code,
      BODY_WEIGHT: METRIC_CONFIG.BODY_WEIGHT.code,
      BODY_TEMPERATURE: METRIC_CONFIG.BODY_TEMPERATURE.code,
      OXYGEN_SATURATION: METRIC_CONFIG.OXYGEN_SATURATION.code,
      BLOOD_GLUCOSE: METRIC_CONFIG.BLOOD_GLUCOSE.code,
      STEPS: METRIC_CONFIG.STEPS.code,
      SLEEP_DURATION: METRIC_CONFIG.SLEEP_DURATION.code,
      SYSTOLIC_BP: METRIC_CONFIG.SYSTOLIC_BP.code,
      DIASTOLIC_BP: METRIC_CONFIG.DIASTOLIC_BP.code,
      CALORIES: METRIC_CONFIG.CALORIES.code,
};

// Helper function to check if an observation matches by LOINC code OR display name
const observationMatches = (entry, metricKey) => {
      const config = METRIC_CONFIG[metricKey];
      if (!config) return false;
      
      const coding = entry.resource?.code?.coding || [];
      const displayName = entry.resource?.code?.coding?.[0]?.display || entry.resource?.code?.text || '';
      
      // Match by LOINC code
      const codeMatch = coding.some(c => c.code === config.code);
      
      // Match by display name (case-insensitive)
      const nameMatch = config.names.some(name => 
            displayName.toLowerCase().includes(name.toLowerCase())
      );
      
      return codeMatch || nameMatch;
};

// Helper function to extract observations by metric key (uses both LOINC and display name)
const extractLatestObservation = (observations, loincCodeOrKey) => {
      if (!observations?.entry) return null;
      
      // Determine the metric key from LOINC code
      let metricKey = null;
      for (const [key, config] of Object.entries(METRIC_CONFIG)) {
            if (config.code === loincCodeOrKey || key === loincCodeOrKey) {
                  metricKey = key;
                  break;
            }
      }
      
      if (!metricKey) return null;
      
      // Find all observations matching this metric (by code OR display name)
      const matchingObs = observations.entry
            .filter(entry => observationMatches(entry, metricKey))
            .map(entry => ({
                  value: entry.resource?.valueQuantity?.value,
                  unit: entry.resource?.valueQuantity?.unit || '',
                  date: entry.resource?.effectiveDateTime,
                  display: entry.resource?.code?.coding?.[0]?.display || '',
            }))
            .filter(obs => obs.value !== undefined)
            .sort((a, b) => new Date(b.date) - new Date(a.date)); // Sort by date descending
      
      return matchingObs.length > 0 ? matchingObs[0] : null;
};

// Helper function to calculate average of observations
const calculateAverage = (observations, loincCodeOrKey) => {
      if (!observations?.entry) return null;
      
      // Determine the metric key from LOINC code
      let metricKey = null;
      for (const [key, config] of Object.entries(METRIC_CONFIG)) {
            if (config.code === loincCodeOrKey || key === loincCodeOrKey) {
                  metricKey = key;
                  break;
            }
      }
      
      if (!metricKey) return null;
      
      const matchingObs = observations.entry
            .filter(entry => observationMatches(entry, metricKey))
            .map(entry => entry.resource?.valueQuantity?.value)
            .filter(value => value !== undefined);
      
      if (matchingObs.length === 0) return null;
      
      const sum = matchingObs.reduce((acc, val) => acc + val, 0);
      return Math.round((sum / matchingObs.length) * 10) / 10; // Round to 1 decimal
};

// Helper function to get observation count
const getObservationCount = (observations, loincCode) => {
      if (!observations?.entry) return 0;
      
      return observations.entry.filter(entry => {
            const coding = entry.resource?.code?.coding || [];
            return coding.some(c => c.code === loincCode);
      }).length;
};

const InsightsWidget = () => {
      const [insightsData, setInsightsData] = useState(null); // Insights data state
      const [rawObservations, setRawObservations] = useState(null); // Store raw observations for analysis
      const [modal, setModal] = useState(false); // Modal visibility state
      const { authUser } = useContext(AuthUserContext); // Current authenticated user
      const [gptAnalysis, setGptAnalysis] = useState(null);
      const [isAnalyzing, setIsAnalyzing] = useState(false);
      const [selectedModal, setSelectedModal] = useState(null);
      const [modalData, setModalData] = useState(null);
      const [modalGptResponse, setModalGptResponse] = useState(null);
      const [loading, setLoading] = useState(true); // Add loading state
      
      // AI Recommendation states
      const [aiSuggestions, setAiSuggestions] = useState([]); // Dynamic AI suggestions based on observations
      const [selectedSuggestion, setSelectedSuggestion] = useState(null); // Currently selected suggestion
      const [suggestionResponse, setSuggestionResponse] = useState(null); // AI response for selected suggestion
      const [isSuggestionLoading, setIsSuggestionLoading] = useState(false); // Loading state for suggestion
      
      // Audio playback states
      const [isAudioPlaying, setIsAudioPlaying] = useState(false); // Audio playback state
      const speechSynthRef = useRef(null); // Reference to current speech utterance
      
      // Memoize date range to prevent infinite re-renders
      // Empty dependency array - only compute once on mount (month won't change during session)
      const { firstDay, lastDay } = useMemo(() => getFirstAndLastDayOfMonth(), []);
      
      // Toggle modal visibility
      const toggleModal = () => setModal(!modal);
      
      // Play AI response as audio using Web Speech API
      const playAudio = (textToSpeak) => {
            if (!textToSpeak || isAudioPlaying) return;
            
            // Check if speech synthesis is supported
            if (!('speechSynthesis' in window)) {
                  toast.error('Text-to-speech is not supported in your browser');
                  return;
            }
            
            // Cancel any ongoing speech
            window.speechSynthesis.cancel();
            
            // Function to start speaking after voices are ready
            const startSpeaking = () => {
                  // Create new utterance
                  const utterance = new SpeechSynthesisUtterance(textToSpeak);
                  utterance.rate = 1.0; // Normal speed
                  utterance.pitch = 1.0; // Normal pitch
                  utterance.volume = 1.0; // Full volume
                  
                  // Try to use a natural-sounding voice
                  const voices = window.speechSynthesis.getVoices();
                  const preferredVoice = voices.find(voice => 
                        voice.name.includes('Google') || 
                        voice.name.includes('Samantha') || 
                        voice.name.includes('Natural')
                  ) || voices.find(voice => voice.lang.startsWith('en')) || voices[0];
                  
                  if (preferredVoice) {
                        utterance.voice = preferredVoice;
                  }
                  
                  // Event handlers
                  utterance.onstart = () => {
                        setIsAudioPlaying(true);
                  };
                  
                  utterance.onend = () => {
                        setIsAudioPlaying(false);
                        speechSynthRef.current = null;
                  };
                  
                  utterance.onerror = (event) => {
                        console.error('Speech synthesis error:', event);
                        setIsAudioPlaying(false);
                        speechSynthRef.current = null;
                        // Only show error for actual failures, not interruptions or cancellations
                        if (event.error !== 'canceled' && event.error !== 'interrupted') {
                              toast.error('Failed to play audio');
                        }
                  };
                  
                  // Store reference and start speaking
                  speechSynthRef.current = utterance;
                  
                  // Small delay to ensure stability after cancel
                  setTimeout(() => {
                        window.speechSynthesis.speak(utterance);
                  }, 100);
            };
            
            // Check if voices are already loaded
            const voices = window.speechSynthesis.getVoices();
            if (voices.length > 0) {
                  startSpeaking();
            } else {
                  // Wait for voices to load (Chrome loads them asynchronously)
                  window.speechSynthesis.onvoiceschanged = () => {
                        startSpeaking();
                  };
                  // Fallback: try anyway after a short delay
                  setTimeout(() => {
                        if (!isAudioPlaying) {
                              startSpeaking();
                        }
                  }, 500);
            }
      };
      
      // Stop audio playback
      const stopAudio = () => {
            if ('speechSynthesis' in window) {
                  window.speechSynthesis.cancel();
            }
            setIsAudioPlaying(false);
            speechSynthRef.current = null;
      };
      
      // Generate dynamic AI suggestions based on available observations
      const generateAISuggestions = (observations) => {
            if (!observations?.entry?.length) return [];
            
            const suggestions = [];
            const observationTypes = {};
            
            // Group observations by type and calculate stats
            observations.entry.forEach(entry => {
                  const displayName = entry.resource?.code?.coding?.[0]?.display || entry.resource?.code?.text || 'Unknown';
                  const value = entry.resource?.valueQuantity?.value;
                  const unit = entry.resource?.valueQuantity?.unit || '';
                  
                  if (!observationTypes[displayName]) {
                        observationTypes[displayName] = {
                              values: [],
                              unit,
                              count: 0
                        };
                  }
                  
                  if (value !== undefined) {
                        observationTypes[displayName].values.push(value);
                        observationTypes[displayName].count++;
                  }
            });
            
            // Generate suggestions based on available observation types
            Object.entries(observationTypes).forEach(([type, data]) => {
                  const avg = data.values.length > 0 
                        ? (data.values.reduce((a, b) => a + b, 0) / data.values.length).toFixed(1) 
                        : null;
                  const min = data.values.length > 0 ? Math.min(...data.values) : null;
                  const max = data.values.length > 0 ? Math.max(...data.values) : null;
                  
                  // Add type-specific suggestions
                  const typeLower = type.toLowerCase();
                  
                  if (typeLower.includes('sleep')) {
                        suggestions.push({
                              id: `sleep-quality`,
                              icon: '😴',
                              label: 'Am I getting enough sleep?',
                              color: 'info',
                              type: type,
                              context: { avg, min, max, count: data.count, unit: data.unit },
                              prompt: `Based on my sleep data (${data.count} entries, average: ${avg} ${data.unit}, range: ${min}-${max} ${data.unit}), am I getting enough sleep? What's the ideal amount and how do I compare? Give me specific recommendations.`
                        });
                  }
                  
                  if (typeLower.includes('step')) {
                        suggestions.push({
                              id: `steps-goal`,
                              icon: '🚶',
                              label: 'Am I hitting my step goals?',
                              color: 'success',
                              type: type,
                              context: { avg, min, max, count: data.count, unit: data.unit },
                              prompt: `My step data shows ${data.count} entries with an average of ${avg} steps/day (range: ${min?.toLocaleString()}-${max?.toLocaleString()}). Am I meeting recommended daily step goals? How can I improve?`
                        });
                  }
                  
                  if (typeLower.includes('heart') || typeLower.includes('hr')) {
                        suggestions.push({
                              id: `heart-health`,
                              icon: '❤️',
                              label: 'Is my heart rate healthy?',
                              color: 'danger',
                              type: type,
                              context: { avg, min, max, count: data.count, unit: data.unit },
                              prompt: `My heart rate data shows ${data.count} readings with an average of ${avg} bpm (range: ${min}-${max} bpm). Is this a healthy resting heart rate? What factors affect it?`
                        });
                  }
                  
                  if (typeLower.includes('weight')) {
                        suggestions.push({
                              id: `weight-trend`,
                              icon: '⚖️',
                              label: 'What\'s my weight trend?',
                              color: 'primary',
                              type: type,
                              context: { avg, min, max, count: data.count, unit: data.unit },
                              prompt: `My weight data shows ${data.count} entries, ranging from ${min} to ${max} ${data.unit} (average: ${avg} ${data.unit}). What does this trend suggest? Is this healthy variation?`
                        });
                  }
                  
                  if (typeLower.includes('calorie')) {
                        suggestions.push({
                              id: `calories-intake`,
                              icon: '🍎',
                              label: 'How are my calories?',
                              color: 'warning',
                              type: type,
                              context: { avg, min, max, count: data.count, unit: data.unit },
                              prompt: `My calorie intake data shows ${data.count} entries with an average of ${avg} kcal/day (range: ${min?.toLocaleString()}-${max?.toLocaleString()} kcal). Is this appropriate for my health? What should I aim for?`
                        });
                  }
                  
                  if (typeLower.includes('glucose') || typeLower.includes('sugar')) {
                        suggestions.push({
                              id: `blood-glucose`,
                              icon: '🩸',
                              label: 'How\'s my blood glucose?',
                              color: 'danger',
                              type: type,
                              context: { avg, min, max, count: data.count, unit: data.unit },
                              prompt: `My blood glucose readings show ${data.count} entries with an average of ${avg} ${data.unit} (range: ${min}-${max} ${data.unit}). Are these levels healthy? What do variations mean?`
                        });
                  }
                  
                  if (typeLower.includes('oxygen') || typeLower.includes('spo2')) {
                        suggestions.push({
                              id: `oxygen-levels`,
                              icon: '🫁',
                              label: 'Are my oxygen levels OK?',
                              color: 'info',
                              type: type,
                              context: { avg, min, max, count: data.count, unit: data.unit },
                              prompt: `My oxygen saturation data shows ${data.count} readings with an average of ${avg}% (range: ${min}-${max}%). Are these levels normal? When should I be concerned?`
                        });
                  }
                  
                  if (typeLower.includes('temperature') || typeLower.includes('temp')) {
                        suggestions.push({
                              id: `body-temp`,
                              icon: '🌡️',
                              label: 'Is my temperature normal?',
                              color: 'warning',
                              type: type,
                              context: { avg, min, max, count: data.count, unit: data.unit },
                              prompt: `My body temperature data shows ${data.count} readings with an average of ${avg}°${data.unit?.includes('C') ? 'C' : 'F'} (range: ${min}-${max}). Is this normal variation? What affects body temperature?`
                        });
                  }
                  
                  if (typeLower.includes('water') || typeLower.includes('hydration')) {
                        suggestions.push({
                              id: `hydration`,
                              icon: '💧',
                              label: 'Am I drinking enough water?',
                              color: 'info',
                              type: type,
                              context: { avg, min, max, count: data.count, unit: data.unit },
                              prompt: `My water intake data shows ${data.count} entries with an average of ${avg} ${data.unit}/day (range: ${min}-${max} ${data.unit}). Am I properly hydrated? What's the recommended daily intake?`
                        });
                  }
                  
                  if (typeLower.includes('blood pressure') || typeLower.includes('systolic') || typeLower.includes('diastolic')) {
                        suggestions.push({
                              id: `blood-pressure`,
                              icon: '💓',
                              label: 'How\'s my blood pressure?',
                              color: 'danger',
                              type: type,
                              context: { avg, min, max, count: data.count, unit: data.unit },
                              prompt: `My blood pressure data shows ${data.count} readings with an average of ${avg} mmHg (range: ${min}-${max} mmHg). Is this in the healthy range? What lifestyle changes could help?`
                        });
                  }
                  
                  // Generic average question for any observation type with multiple entries
                  if (data.count >= 3 && !suggestions.find(s => s.type === type)) {
                        suggestions.push({
                              id: `avg-${type.replace(/\s+/g, '-').toLowerCase()}`,
                              icon: '📊',
                              label: `What's my average ${type}?`,
                              color: 'secondary',
                              type: type,
                              context: { avg, min, max, count: data.count, unit: data.unit },
                              prompt: `Analyze my ${type} data: ${data.count} entries with an average of ${avg} ${data.unit} (range: ${min}-${max} ${data.unit}). What does this mean for my health? Any recommendations?`
                        });
                  }
            });
            
            // Add general wellness suggestion if we have enough data
            if (Object.keys(observationTypes).length >= 3) {
                  suggestions.push({
                        id: 'overall-health',
                        icon: '🏥',
                        label: 'How\'s my overall health?',
                        color: 'primary',
                        type: 'overall',
                        context: { totalTypes: Object.keys(observationTypes).length, totalEntries: observations.entry.length },
                        prompt: `Based on all my health observations (${observations.entry.length} total entries across ${Object.keys(observationTypes).length} different metrics), give me an overall health assessment and top 3 actionable recommendations.`
                  });
            }
            
            // Remove duplicates and return
            const uniqueSuggestions = suggestions.filter((suggestion, index, self) =>
                  index === self.findIndex(s => s.id === suggestion.id)
            );
            
            return uniqueSuggestions.slice(0, 8); // Limit to 8 suggestions
      };
      
      // Handle clicking on an AI suggestion
      const handleSuggestionClick = async (suggestion) => {
            setSelectedSuggestion(suggestion);
            setSuggestionResponse(null);
            setIsSuggestionLoading(true);
            
            try {
                  const systemMessage = `You are a friendly health assistant providing personalized insights based on the user's actual health data. Be encouraging but honest. Keep responses concise (2-3 paragraphs max). Include specific numbers from their data. End with 1-2 actionable tips. Always remind them to consult a healthcare professional for medical advice.`;
                  
                  const response = await callOpenAI(suggestion.prompt, systemMessage);
                  setSuggestionResponse(response);
            } catch (error) {
                  console.error('Error getting AI suggestion response:', error);
                  toast.error('Failed to get AI insights');
                  setSuggestionResponse('Sorry, I couldn\'t generate insights right now. Please try again.');
            } finally {
                  setIsSuggestionLoading(false);
            }
      };
      
      // Close suggestion modal
      const closeSuggestionModal = () => {
            stopAudio(); // Stop audio when closing
            setSelectedSuggestion(null);
            setSuggestionResponse(null);
      };

      // Function to format observations for GPT
      const formatObservationsForGPT = (observations) => {
            if (!observations || !observations.entry) {
                  return 'No health data available for analysis.';
            }

            const formattedData = observations.entry
                  .filter(entry => entry.resource && entry.resource.valueQuantity) // Only include entries with values
                  .map(entry => {
                        const resource = entry.resource;

                        // Extract date with error handling
                        const date = resource.effectiveDateTime ?
                              new Date(resource.effectiveDateTime).toLocaleDateString('en-US', {
                                    month: 'short',
                                    day: 'numeric',
                                    year: 'numeric'
                              }) : 'No date';

                        // Get observation type and value with better error handling
                        const observationType = resource.code?.coding?.[0]?.display ||
                              resource.code?.coding?.[0]?.code ||
                              'Unknown type';

                        const value = resource.valueQuantity?.value || 'No value';
                        const unit = resource.valueQuantity?.unit || '';

                        return `${observationType}: ${value}${unit} on ${date}`;
                  })
                  .filter(Boolean) // Remove any null entries
                  .join('\n');

            // If no valid observations were found
            if (!formattedData) {
                  return 'No valid health observations found for analysis.';
            }

            return `Analyze the following health observations and generate a short, personalized summary with insights.
Data:

${formattedData}

Consider:
- Optimal health ranges for each metric
- Patterns and trends over time
- Balance between different health aspects
- Potential areas for improvement
- Notable achievements or concerns

Please provide specific, actionable insights based on this data.`;
      };

      // Function to analyze observations with OpenAI GPT
      const analyzeWithGPT = async (observations) => {
            setIsAnalyzing(true);
            try {
                  const prompt = formatObservationsForGPT(observations);
                  const analysisResult = await callOpenAI(prompt);
                  setGptAnalysis(analysisResult);
            } catch (error) {
                  console.error('Error analyzing observations:', error);
                  toast.error('Failed to analyze health data');
            } finally {
                  setIsAnalyzing(false);
            }
      };

      // Fetch insights from FHIR and other sources
      useEffect(() => {
            const fetchInsights = async () => {
                  setLoading(true); // Start loading
                  try {
                        const healthResponse = await axiosGetWithFhirRetry(
                              `${fhirEndpoint}/Observation?date=ge${firstDay.toISOString()}&date=le${lastDay.toISOString()}&identifier=${encodeURIComponent(`${fhirIdSystem}|${authUser.uid}`)}`
                        );
                        
                        // Store raw observations for GPT analysis
                        setRawObservations(healthResponse.data);
                        
                        // Generate AI suggestions based on available observations
                        const suggestions = generateAISuggestions(healthResponse.data);
                        setAiSuggestions(suggestions);

                        // Extract actual observation values from FHIR data
                        const observations = healthResponse.data;
                        const hasObservations = observations?.entry?.length > 0;

                        // Extract latest values for each metric
                        const latestWeight = extractLatestObservation(observations, LOINC_CODES.BODY_WEIGHT);
                        const latestHeartRate = extractLatestObservation(observations, LOINC_CODES.HEART_RATE);
                        const latestTemp = extractLatestObservation(observations, LOINC_CODES.BODY_TEMPERATURE);
                        const latestO2 = extractLatestObservation(observations, LOINC_CODES.OXYGEN_SATURATION);
                        const latestGlucose = extractLatestObservation(observations, LOINC_CODES.BLOOD_GLUCOSE);
                        const latestSteps = extractLatestObservation(observations, LOINC_CODES.STEPS);
                        const latestSleep = extractLatestObservation(observations, LOINC_CODES.SLEEP_DURATION);
                        const latestCalories = extractLatestObservation(observations, LOINC_CODES.CALORIES);
                        const latestSystolic = extractLatestObservation(observations, LOINC_CODES.SYSTOLIC_BP);
                        const latestDiastolic = extractLatestObservation(observations, LOINC_CODES.DIASTOLIC_BP);

                        // Calculate averages for trending
                        const avgHeartRate = calculateAverage(observations, LOINC_CODES.HEART_RATE);
                        const avgSteps = calculateAverage(observations, LOINC_CODES.STEPS);
                        const avgSleep = calculateAverage(observations, LOINC_CODES.SLEEP_DURATION);

                        // Get observation counts for activity tracking
                        const totalObservations = observations?.entry?.length || 0;

                        // Build health metrics from actual data
                        const healthMetrics = {
                              // Primary metrics (always shown)
                              weight: latestWeight?.value || null,
                              weightUnit: latestWeight?.unit || 'kg',
                              heartRate: latestHeartRate?.value || null,
                              heartRateUnit: latestHeartRate?.unit || 'bpm',
                              
                              // Secondary metrics
                              temperature: latestTemp?.value || null,
                              temperatureUnit: latestTemp?.unit || '°F',
                              oxygenSaturation: latestO2?.value || null,
                              bloodGlucose: latestGlucose?.value || null,
                              glucoseUnit: latestGlucose?.unit || 'mg/dL',
                              
                              // Activity metrics
                              steps: latestSteps?.value || null,
                              stepsAvg: avgSteps,
                              sleep: latestSleep?.value || null,
                              sleepAvg: avgSleep,
                              calories: latestCalories?.value || null,
                              
                              // Blood pressure
                              systolic: latestSystolic?.value || null,
                              diastolic: latestDiastolic?.value || null,
                              
                              // Averages for insights
                              avgHeartRate: avgHeartRate,
                              
                              // Data availability flags
                              hasData: hasObservations,
                              totalObservations: totalObservations,
                              lastUpdated: observations?.entry?.[0]?.resource?.effectiveDateTime || null,
                        };

                        // Fetch ChargeItem (expenses) data
                        let totalExpenses = 0;
                        let expenseCount = 0;
                        try {
                              const chargeResponse = await axiosGetWithFhirRetry(
                                    `${fhirEndpoint}/ChargeItem?identifier=${encodeURIComponent(`${fhirIdSystem}|${authUser.uid}`)}&_count=500`
                              );
                              
                              if (chargeResponse.data?.entry?.length > 0) {
                                    expenseCount = chargeResponse.data.entry.length;
                                    totalExpenses = chargeResponse.data.entry.reduce((sum, entry) => {
                                          return sum + (entry.resource?.priceOverride?.value || 0);
                                    }, 0);
                              }
                        } catch (chargeError) {
                              console.warn('Could not fetch ChargeItem data:', chargeError.message);
                              // Continue without expense data - not critical
                        }

                        // Fetch mood data from Firebase Realtime DB
                        let moodCounts = { Happy: 0, Sad: 0, Angry: 0, Anxious: 0, Calm: 0 };
                        let latestMood = null;
                        let totalMoodEntries = 0;
                        try {
                              const db = getDatabase();
                              const moodRef = ref(db, `moodObservations/${authUser.uid}`);
                              const moodSnapshot = await get(moodRef);
                              const moodVal = moodSnapshot.val();
                              if (moodVal) {
                                    const entries = Object.values(moodVal);
                                    totalMoodEntries = entries.length;
                                    entries.forEach(e => {
                                          if (moodCounts[e.display] !== undefined) moodCounts[e.display]++;
                                    });
                                    // Latest mood = most recent by effectiveDateTime
                                    latestMood = entries
                                          .filter(e => e.effectiveDateTime)
                                          .sort((a, b) => new Date(b.effectiveDateTime) - new Date(a.effectiveDateTime))[0]?.display || null;
                              }
                        } catch (moodError) {
                              console.warn('Could not fetch mood data:', moodError.message);
                        }

                        // Set insights data with real values
                        setInsightsData({
                              health: healthMetrics,
                              financials: totalExpenses,
                              financialsLoaded: true,
                              expenseCount: expenseCount,
                              mood: { counts: moodCounts, latest: latestMood, total: totalMoodEntries },
                              robots: { active: 0, total: 0, tasksCompleted: 0 },
                              metaverse: { level: 0, questsCompleted: 0 },
                        });
                  } catch (error) {
                        console.error('Error fetching insights data:', error);
                        // Set empty data on error (no fake defaults)
                        setInsightsData({
                              health: {
                                    weight: null,
                                    heartRate: null,
                                    hasData: false,
                                    totalObservations: 0,
                              },
                              financials: 0,
                              financialsLoaded: true,
                              expenseCount: 0,
                              mood: { counts: { Happy: 0, Sad: 0, Angry: 0, Anxious: 0, Calm: 0 }, latest: null, total: 0 },
                              robots: { active: 0, total: 0, tasksCompleted: 0 },
                              metaverse: { level: 0, questsCompleted: 0 },
                        });
                  } finally {
                        setLoading(false); // End loading regardless of outcome
                  }
            };

            if (authUser) {
                  fetchInsights();
            } else {
                  setLoading(false);
            }
      }, [authUser, firstDay, lastDay]); // Add dependencies

      // Helper to format the last updated date
      const formatLastUpdated = (dateString) => {
            if (!dateString) return 'Never';
            const date = new Date(dateString);
            const now = new Date();
            const diffMs = now - date;
            const diffMins = Math.floor(diffMs / 60000);
            const diffHours = Math.floor(diffMs / 3600000);
            const diffDays = Math.floor(diffMs / 86400000);
            
            if (diffMins < 60) return `${diffMins} min ago`;
            if (diffHours < 24) return `${diffHours}h ago`;
            if (diffDays < 7) return `${diffDays}d ago`;
            return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      };

      // Generate chart data from actual observations
      const generateHealthChartData = () => {
            const health = insightsData?.health;
            if (!health?.hasData) {
                  return {
                        labels: ['No Data'],
                        datasets: [{ data: [1], backgroundColor: ['rgba(255,255,255,0.1)'] }]
                  };
            }
            
            const labels = [];
            const data = [];
            const colors = [];
            
            // Add available metrics to chart
            if (health.weight) {
                  labels.push(`Weight (${health.weightUnit})`);
                  data.push(health.weight);
                  colors.push('#FF6384');
            }
            if (health.heartRate) {
                  labels.push(`Heart Rate (${health.heartRateUnit})`);
                  data.push(health.heartRate);
                  colors.push('#36A2EB');
            }
            if (health.oxygenSaturation) {
                  labels.push('O₂ Sat (%)');
                  data.push(health.oxygenSaturation);
                  colors.push('#4BC0C0');
            }
            if (health.temperature) {
                  labels.push(`Temp (${health.temperatureUnit})`);
                  data.push(health.temperature);
                  colors.push('#FF9F40');
            }
            
            // If no data, show placeholder
            if (labels.length === 0) {
                  return {
                        labels: ['No Data'],
                        datasets: [{ data: [1], backgroundColor: ['rgba(255,255,255,0.1)'] }]
                  };
            }
            
            return {
                  labels,
                  datasets: [{ data, backgroundColor: colors }]
            };
      };

      // Function to handle viewing analyzed data
      // Function to handle viewing analyzed data using OpenAI
      const handleViewAnalysis = async (type) => {
            try {
                  setIsAnalyzing(true);
                  setSelectedModal(type);

                  let analysisResult;
                  switch (type) {
                        case 'health':
                              // Use already-fetched observations instead of refetching
                              if (!rawObservations || !rawObservations.entry?.length) {
                                    toast.warning('No health observations available for analysis');
                                    setIsAnalyzing(false);
                                    return;
                              }
                              const healthPrompt = formatObservationsForGPT(rawObservations);
                              analysisResult = await callOpenAI(healthPrompt);
                              break;

                        case 'financials':
                              try {
                                    // ChargeItem uses 'occurrence' parameter for date filtering, not 'date'
                                    const financeResponse = await axiosGetWithFhirRetry(
                                          `${fhirEndpoint}/ChargeItem?identifier=${encodeURIComponent(`${fhirIdSystem}|${authUser.uid}`)}&_sort=-_lastUpdated&_count=100`
                                    );
                                    
                                    // Check if we have any expense data
                                    if (!financeResponse.data?.entry?.length) {
                                          analysisResult = 'No expense data found. Load demo expenses from the Admin page to see financial insights.';
                                    } else {
                                          // Format expenses for GPT analysis
                                          const expenses = financeResponse.data.entry.map(e => ({
                                                description: e.resource?.code?.text || e.resource?.code?.coding?.[0]?.display || 'Unknown',
                                                category: e.resource?.extension?.find(ext => ext.url?.includes('expense-category'))?.valueString || 'Uncategorized',
                                                amount: e.resource?.priceOverride?.value || 0,
                                                currency: e.resource?.priceOverride?.currency || 'USD',
                                                date: e.resource?.occurrenceDateTime || e.resource?.enteredDate
                                          }));
                                          
                                          const totalSpent = expenses.reduce((sum, e) => sum + e.amount, 0);
                                          const byCategory = expenses.reduce((acc, e) => {
                                                acc[e.category] = (acc[e.category] || 0) + e.amount;
                                                return acc;
                                          }, {});
                                          
                                          const financePrompt = `Analyze my spending data and provide financial insights:
                                          
Total Expenses: $${totalSpent.toFixed(2)}
Number of Transactions: ${expenses.length}

Spending by Category:
${Object.entries(byCategory).map(([cat, amt]) => `- ${cat}: $${amt.toFixed(2)}`).join('\n')}

Recent Transactions:
${expenses.slice(0, 15).map(e => `- ${e.description}: $${e.amount.toFixed(2)} (${e.category})`).join('\n')}

Please provide insights on spending patterns, potential savings opportunities, and financial health recommendations.`;
                                          
                                          analysisResult = await callOpenAI(
                                                financePrompt,
                                                'You are a helpful financial insights assistant. Provide concise, actionable financial advice based on the data provided. Be friendly and encouraging. Always remind users to consult financial professionals for major decisions.'
                                          );
                                    }
                              } catch (financeError) {
                                    console.error('Error fetching ChargeItem:', financeError);
                                    analysisResult = 'Unable to fetch expense data. The FHIR server may not support ChargeItem resources, or no expenses have been recorded yet. Try loading demo expenses from the Admin page.';
                              }
                              break;
                        case 'mood':
                              if (!insightsData.mood.total) {
                                    analysisResult = 'No mood data recorded yet. Log your mood from the Mood Ring page to get personalized emotional wellness insights.';
                              } else {
                                    const { counts, latest, total } = insightsData.mood;
                                    const moodPrompt = `Analyze my mood data and provide emotional wellness insights:

Total mood entries: ${total}
Latest mood: ${latest}

Mood distribution:
${Object.entries(counts).filter(([, v]) => v > 0).map(([mood, count]) => `- ${mood}: ${count} times (${Math.round((count / total) * 100)}%)`).join('\n')}

Please provide insights on my emotional wellbeing patterns, what they might indicate, and practical tips for emotional balance. Be warm, empathetic, and encouraging. Remind users to seek professional support if needed.`;
                                    analysisResult = await callOpenAI(
                                          moodPrompt,
                                          'You are MiEROS, a compassionate AI wellness companion. Analyze mood data and provide warm, empathetic emotional wellness insights. Be supportive and offer practical mindfulness and self-care tips. Always recommend professional mental health support when appropriate.'
                                    );
                              }
                              break;

                        default:
                              toast.error('Unknown analysis type');
                              setIsAnalyzing(false);
                              return;
                  }

                  setModalGptResponse(analysisResult);
            } catch (error) {
                  console.error(`Error analyzing ${type} data:`, error);
                  toast.error(`Failed to analyze ${type} data`);
            } finally {
                  setIsAnalyzing(false);
            }
      };

      // Update the format function to handle bullet points better
      const formatGptResponse = (text) => {
            if (!text) return '';

            // Model output is untrusted and rendered via dangerouslySetInnerHTML: escape it first (prevents XSS).
            text = String(text)
                  .replace(/&/g, '&amp;')
                  .replace(/</g, '&lt;')
                  .replace(/>/g, '&gt;')
                  .replace(/"/g, '&quot;')
                  .replace(/'/g, '&#39;');

            // Split into sections based on emojis or paragraph breaks
            const sections = text.split(/(?=🧝‍♀️|��|😌|[\n]{2,})/g);

            // Format each section
            const formattedSections = sections.map(section => {
                  // Remove any leading/trailing whitespace
                  section = section.trim();

                  // Check if section contains bullet points
                  if (section.includes('• ')) {
                        // Split the section into lines
                        const lines = section.split('\n');

                        // Format lines with bullet points into a list
                        const formattedLines = lines.map(line => {
                              if (line.includes('• ')) {
                                    // Split line at bullet point and wrap in list item
                                    const [_, content] = line.split('• ');
                                    return `<li>${content.trim()}</li>`;
                              }
                              return `<p>${line}</p>`;
                        });

                        // Join lines back together, wrapping bullet points in a list
                        section = formattedLines.map((line, index, array) => {
                              if (line.startsWith('<li>')) {
                                    // If this is the first list item, start a list
                                    if (!array[index - 1]?.startsWith('<li>')) {
                                          return `<ul>${line}`;
                                    }
                                    // If this is the last list item, end the list
                                    if (!array[index + 1]?.startsWith('<li>')) {
                                          return `${line}</ul>`;
                                    }
                                    return line;
                              }
                              return line;
                        }).join('');
                  }

                  // Add paragraph styling
                  return `<div class="analysis-paragraph">${section}</div>`;
            });

            return formattedSections.join('');
      };

      // Early return for loading state
      if (loading) {
            return (
                  <CCard className="insights-widget">
                        <div className="mb-2">Loading insights...</div>
                        {/* Add a loading spinner or skeleton here if desired */}
                  </CCard>
            );
      }

      return (
            <CCard className="insights-widget">
                  <div className="insights-container">
                        {insightsData ? (
                              <>
                                    <div className="insight-section">
                                          <h5>Health</h5>
                                          {insightsData.health.hasData ? (
                                                <>
                                                      <CChartPie
                                                            data={generateHealthChartData()}
                                                            options={{ ...pieChartOptions, maintainAspectRatio: true }}
                                                            style={{ maxHeight: '150px', maxWidth: '150px', margin: 'auto' }}
                                                      />
                                                      <div className="insight-details">
                                                            {insightsData.health.weight && (
                                                                  <p>Weight: {insightsData.health.weight} {insightsData.health.weightUnit}</p>
                                                            )}
                                                            {insightsData.health.heartRate && (
                                                                  <p>Heart Rate: {insightsData.health.heartRate} {insightsData.health.heartRateUnit}
                                                                        {insightsData.health.avgHeartRate && (
                                                                              <span className="mb-2 small"> (avg: {insightsData.health.avgHeartRate})</span>
                                                                        )}
                                                                  </p>
                                                            )}
                                                            {insightsData.health.oxygenSaturation && (
                                                                  <p>O₂ Saturation: {insightsData.health.oxygenSaturation}%</p>
                                                            )}
                                                            {insightsData.health.temperature && (
                                                                  <p>Temperature: {insightsData.health.temperature} {insightsData.health.temperatureUnit}</p>
                                                            )}
                                                            {insightsData.health.bloodGlucose && (
                                                                  <p>Blood Glucose: {insightsData.health.bloodGlucose} {insightsData.health.glucoseUnit}</p>
                                                            )}
                                                            {(insightsData.health.systolic && insightsData.health.diastolic) && (
                                                                  <p>Blood Pressure: {insightsData.health.systolic}/{insightsData.health.diastolic} mmHg</p>
                                                            )}
                                                            {insightsData.health.steps && (
                                                                  <p>Steps: {insightsData.health.steps.toLocaleString()}
                                                                        {insightsData.health.stepsAvg && (
                                                                              <span className="mb-2 small"> (avg: {Math.round(insightsData.health.stepsAvg).toLocaleString()})</span>
                                                                        )}
                                                                  </p>
                                                            )}
                                                            {insightsData.health.sleep && (
                                                                  <p>Sleep: {insightsData.health.sleep} hrs
                                                                        {insightsData.health.sleepAvg && (
                                                                              <span className="mb-2 small"> (avg: {insightsData.health.sleepAvg}h)</span>
                                                                        )}
                                                                  </p>
                                                            )}
                                                            {insightsData.health.calories && (
                                                                  <p>Calories: {insightsData.health.calories.toLocaleString()} kcal</p>
                                                            )}
                                                            <small className="mb-2 d-block mt-1">
                                                                  {insightsData.health.totalObservations} observations · Updated {formatLastUpdated(insightsData.health.lastUpdated)}
                                                            </small>
                                                      </div>
                                                </>
                                          ) : (
                                                <div className="no-data-placeholder text-center py-3">
                                                      <p className="mb-2">No health data available</p>
                                                      <small className="mb-2">
                                                            Add observations from the Health Dashboard or Admin page
                                                      </small>
                                                </div>
                                          )}
                                          <CButton
                                                color="secondary"
                                                size="sm"
                                                onClick={() => handleViewAnalysis('health')}
                                                className="mt-2"
                                                disabled={!insightsData.health.hasData}
                                          >
                                                AI Health Analysis
                                          </CButton>
                                    </div>
                                    
                                    <div className="insight-section">
                                          <h5>Financials</h5>
                                          <CChartBar
                                                data={{
                                                      labels: ['Total Expenses ($)'],
                                                      datasets: [
                                                            {
                                                                  label: 'Expenses',
                                                                  data: [insightsData.financials],
                                                                  backgroundColor: '#FFCE56',
                                                            },
                                                      ],
                                                }}
                                                options={barChartOptions}
                                                style={{ height: '100px', width: '180px', margin: 'auto' }}
                                          />
                                          <div className="insight-details">
                                                {insightsData.financialsLoaded ? (
                                                      insightsData.financials > 0 ? (
                                                            <>
                                                                  <p>Total Expenses: ${insightsData.financials.toLocaleString()}</p>
                                                                  <small className="mb-2">{insightsData.expenseCount || 0} transactions</small>
                                                            </>
                                                      ) : (
                                                            <p className="mb-2">No expenses recorded</p>
                                                      )
                                                ) : (
                                                      <div className="placeholder-text">Loading financial data...</div>
                                                )}
                                          </div>
                                          <CButton
                                                color="secondary"
                                                size="sm"
                                                onClick={() => handleViewAnalysis('financials')}
                                                className="mt-2"
                                          >
                                                AI Financial Analysis
                                          </CButton>
                                    </div>

                                    <div className="insight-section">
                                          <h5>Mood Ring</h5>
                                          <CChartDoughnut
                                                data={{
                                                      labels: ['Happy', 'Calm', 'Anxious', 'Sad', 'Angry'],
                                                      datasets: [
                                                            {
                                                                  data: [
                                                                        insightsData.mood.counts.Happy,
                                                                        insightsData.mood.counts.Calm,
                                                                        insightsData.mood.counts.Anxious,
                                                                        insightsData.mood.counts.Sad,
                                                                        insightsData.mood.counts.Angry,
                                                                  ],
                                                                  backgroundColor: ['#10B981', '#00D4FF', '#F59E0B', '#7C3AED', '#EF4444'],
                                                                  borderWidth: 0,
                                                            },
                                                      ],
                                                }}
                                                options={{ ...doughnutChartOptions, maintainAspectRatio: true }}
                                                style={{ maxHeight: '150px', maxWidth: '150px', margin: 'auto' }}
                                          />
                                          <div className="insight-details">
                                                {insightsData.mood.total > 0 ? (
                                                      <>
                                                            <p>Latest: <strong>{insightsData.mood.latest}</strong></p>
                                                            <small className="mb-2">{insightsData.mood.total} mood entries recorded</small>
                                                      </>
                                                ) : (
                                                      <div className="no-data-placeholder text-center py-2">
                                                            <p className="mb-2 mb-1">No mood data yet</p>
                                                            <small className="mb-2">Log your mood from the Mood Ring page</small>
                                                      </div>
                                                )}
                                          </div>
                                          <CButton
                                                color="secondary"
                                                size="sm"
                                                onClick={() => handleViewAnalysis('mood')}
                                                className="mt-2"
                                                disabled={!insightsData.mood.total}
                                          >
                                                AI Mood Analysis
                                          </CButton>
                                    </div>

                                    <div className="insight-section">
                                          <h5>Robotics</h5>
                                          <CChartDoughnut
                                                data={{
                                                      labels: ['Active', 'Inactive'],
                                                      datasets: [
                                                            {
                                                                  data: [insightsData.robots.active, insightsData.robots.total - insightsData.robots.active],
                                                                  backgroundColor: ['#4CAF50', '#F44336'],
                                                            },
                                                      ],
                                                }}
                                                options={doughnutChartOptions}
                                                style={{ height: '100px', width: '100px', margin: 'auto' }}
                                          />
                                          <div className="insight-details">
                                                {insightsData.robots ? (
                                                      <p>
                                                            Active Robots: {insightsData.robots.active}<br />
                                                            Tasks Completed: {insightsData.robots.tasksCompleted}
                                                      </p>
                                                ) : (
                                                      <div className="placeholder-text">Loading robotics data...</div>
                                                )}
                                          </div>
                                    </div>

                                    <div className="insight-section">
                                          <h5>Metaverse</h5>
                                          <CChartLine
                                                data={{
                                                      labels: ['Level', 'Quests Completed'],
                                                      datasets: [
                                                            {
                                                                  label: 'Level',
                                                                  data: [insightsData.metaverse.level],
                                                                  borderColor: '#FF6384',
                                                                  borderWidth: 2,
                                                                  fill: false,
                                                            },
                                                            {
                                                                  label: 'Quests Completed',
                                                                  data: [insightsData.metaverse.questsCompleted],
                                                                  borderColor: '#36A2EB',
                                                                  borderWidth: 2,
                                                                  fill: false,
                                                            },
                                                      ],
                                                }}
                                                options={lineChartOptions}
                                          />
                                          <div className="insight-details">
                                                {insightsData.metaverse ? (
                                                      <p>
                                                            Level: {insightsData.metaverse.level}<br />
                                                            Quests Completed: {insightsData.metaverse.questsCompleted}
                                                      </p>
                                                ) : (
                                                      <div className="placeholder-text">Loading metaverse data...</div>
                                                )}
                                          </div>
                                    </div>
                              </>
                        ) : (
                              <div className="loading-container">
                                    <p>Loading all insights...</p>
                              </div>
                        )}
                  </div>

                  <div className="view-larger-btn">
                        <CButton
                              color="primary"
                              size="sm"
                              onClick={toggleModal}
                              style={{ fontSize: '10px', padding: '2px 6px' }}
                        >
                              View Larger
                        </CButton>
                  </div>

                  <CModal visible={modal} onClose={() => setModal(false)} size="lg">
                        <CModalHeader closeButton>Detailed Health Insights</CModalHeader>
                        <CModalBody>
                              {insightsData?.health?.hasData ? (
                                    <div>
                                          <h5>Health Metrics - {new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</h5>
                                          <div className="row">
                                                <div className="col-md-6">
                                                      <CChartPie
                                                            data={generateHealthChartData()}
                                                            options={pieChartOptions}
                                                      />
                                                </div>
                                                <div className="col-md-6">
                                                      <h6>Latest Values</h6>
                                                      <table className="table table-sm">
                                                            <tbody>
                                                                  {insightsData.health.weight && (
                                                                        <tr>
                                                                              <td><strong>Weight</strong></td>
                                                                              <td>{insightsData.health.weight} {insightsData.health.weightUnit}</td>
                                                                        </tr>
                                                                  )}
                                                                  {insightsData.health.heartRate && (
                                                                        <tr>
                                                                              <td><strong>Heart Rate</strong></td>
                                                                              <td>{insightsData.health.heartRate} {insightsData.health.heartRateUnit}</td>
                                                                        </tr>
                                                                  )}
                                                                  {insightsData.health.oxygenSaturation && (
                                                                        <tr>
                                                                              <td><strong>O₂ Saturation</strong></td>
                                                                              <td>{insightsData.health.oxygenSaturation}%</td>
                                                                        </tr>
                                                                  )}
                                                                  {insightsData.health.temperature && (
                                                                        <tr>
                                                                              <td><strong>Temperature</strong></td>
                                                                              <td>{insightsData.health.temperature} {insightsData.health.temperatureUnit}</td>
                                                                        </tr>
                                                                  )}
                                                                  {insightsData.health.bloodGlucose && (
                                                                        <tr>
                                                                              <td><strong>Blood Glucose</strong></td>
                                                                              <td>{insightsData.health.bloodGlucose} {insightsData.health.glucoseUnit}</td>
                                                                        </tr>
                                                                  )}
                                                                  {(insightsData.health.systolic && insightsData.health.diastolic) && (
                                                                        <tr>
                                                                              <td><strong>Blood Pressure</strong></td>
                                                                              <td>{insightsData.health.systolic}/{insightsData.health.diastolic} mmHg</td>
                                                                        </tr>
                                                                  )}
                                                                  {insightsData.health.steps && (
                                                                        <tr>
                                                                              <td><strong>Steps</strong></td>
                                                                              <td>{insightsData.health.steps.toLocaleString()}</td>
                                                                        </tr>
                                                                  )}
                                                                  {insightsData.health.sleep && (
                                                                        <tr>
                                                                              <td><strong>Sleep</strong></td>
                                                                              <td>{insightsData.health.sleep} hours</td>
                                                                        </tr>
                                                                  )}
                                                                  {insightsData.health.calories && (
                                                                        <tr>
                                                                              <td><strong>Calories</strong></td>
                                                                              <td>{insightsData.health.calories.toLocaleString()} kcal</td>
                                                                        </tr>
                                                                  )}
                                                            </tbody>
                                                      </table>
                                                      <p className="mb-2 small mt-3">
                                                            Based on {insightsData.health.totalObservations} observations this month
                                                      </p>
                                                </div>
                                          </div>
                                    </div>
                              ) : (
                                    <div className="text-center py-4">
                                          <p className="mb-2">No health observations found for this month.</p>
                                          <p className="mb-2 small">
                                                Add observations from the Health Dashboard or load demo data from the Admin page.
                                          </p>
                                    </div>
                              )}
                        </CModalBody>
                        <CModalFooter>
                              <CButton color="secondary" size="sm" onClick={toggleModal}>
                                    Close
                              </CButton>
                        </CModalFooter>
                  </CModal>

                  {/* Modal for viewing GPT analysis */}
                  <CModal
                        visible={selectedModal !== null}
                        onClose={() => {
                              setSelectedModal(null);
                              setModalGptResponse(null);
                        }}
                        size="lg"
                  >
                        <CModalHeader closeButton>
                              <CModalTitle>
                                    {selectedModal === 'health' ? 'Health Analysis' : selectedModal === 'mood' ? 'Mood Analysis' : 'Financial Analysis'}
                              </CModalTitle>
                        </CModalHeader>
                        <CModalBody>
                              <div className="disclaimer-box mb-3" style={{
                                    backgroundColor: '#eef6ff',
                                    padding: '10px 15px',
                                    borderRadius: '8px',
                                    borderLeft: '4px solid #2563eb'
                              }}>
                                    <small style={{ color: '#1e293b' }}>
                                          <strong>Disclaimer:</strong> This analysis is generated by AI and is for informational purposes only.
                                          Do not use this for professional medical or financial decisions. Always consult with qualified
                                          professionals for advice specific to your situation.
                                    </small>
                              </div>
                              <div className="analysis-container">
                                    {isAnalyzing ? (
                                          <div className="text-center p-4">
                                                <div className="analyzing-spinner">Analyzing your data...</div>
                                          </div>
                                    ) : modalGptResponse ? (
                                          <div
                                                className="analysis-content"
                                                dangerouslySetInnerHTML={{
                                                      __html: formatGptResponse(modalGptResponse)
                                                }}
                                          />
                                    ) : (
                                          <div className="text-center">
                                                <p>No analysis available</p>
                                          </div>
                                    )}
                              </div>
                        </CModalBody>
                        <CModalFooter>
                              {/* Audio Play/Stop Button */}
                              <CButton
                                    color={isAudioPlaying ? 'danger' : 'success'}
                                    size="sm"
                                    onClick={() => isAudioPlaying ? stopAudio() : playAudio(modalGptResponse)}
                                    disabled={isAnalyzing || !modalGptResponse}
                                    style={{ marginRight: 'auto' }}
                              >
                                    {isAudioPlaying ? '⏹️ Stop Audio' : '🔊 Play Audio'}
                              </CButton>
                              <CButton
                                    color="secondary"
                                    onClick={() => {
                                          stopAudio(); // Stop audio when closing
                                          setSelectedModal(null);
                                          setModalGptResponse(null);
                                    }}
                              >
                                    Close
                              </CButton>
                        </CModalFooter>
                  </CModal>
                  
                  {/* Modal for AI Suggestion Response */}
                  <CModal
                        visible={selectedSuggestion !== null}
                        onClose={closeSuggestionModal}
                        size="lg"
                  >
                        <CModalHeader closeButton>
                              <CModalTitle>
                                    {selectedSuggestion?.icon} {selectedSuggestion?.label}
                              </CModalTitle>
                        </CModalHeader>
                        <CModalBody>
                              <div className="disclaimer-box mb-3" style={{
                                    backgroundColor: '#eef6ff',
                                    padding: '10px 15px',
                                    borderRadius: '8px',
                                    borderLeft: '4px solid #0ea5e9'
                              }}>
                                    <small style={{ color: '#1e293b' }}>
                                          <strong>💡 AI Insight</strong> - Based on your {selectedSuggestion?.context?.count || 'health'} observations.
                                          Always consult a healthcare professional for medical advice.
                                    </small>
                              </div>
                              
                              {/* Data Summary */}
                              {selectedSuggestion?.context && selectedSuggestion?.type !== 'overall' && (
                                    <div className="data-summary mb-3" style={{
                                          display: 'flex',
                                          gap: '15px',
                                          flexWrap: 'wrap',
                                          padding: '10px',
                                          backgroundColor: 'var(--primary-light)',
                                          borderRadius: '8px'
                                    }}>
                                          <div>
                                                <small className="mb-2">Average</small>
                                                <div><strong>{selectedSuggestion.context.avg} {selectedSuggestion.context.unit}</strong></div>
                                          </div>
                                          <div>
                                                <small className="mb-2">Min</small>
                                                <div><strong>{selectedSuggestion.context.min}</strong></div>
                                          </div>
                                          <div>
                                                <small className="mb-2">Max</small>
                                                <div><strong>{selectedSuggestion.context.max}</strong></div>
                                          </div>
                                          <div>
                                                <small className="mb-2">Entries</small>
                                                <div><strong>{selectedSuggestion.context.count}</strong></div>
                                          </div>
                                    </div>
                              )}
                              
                              {/* AI Response */}
                              <div className="ai-response" style={{
                                    minHeight: '150px',
                                    padding: '15px',
                                    backgroundColor: 'var(--glass-bg)',
                                    borderRadius: '8px',
                                    border: '1px solid var(--border-default)'
                              }}>
                                    {isSuggestionLoading ? (
                                          <div className="text-center p-4">
                                                <div className="spinner-border text-primary mb-2" role="status">
                                                      <span className="visually-hidden">Loading...</span>
                                                </div>
                                                <p className="mb-2">Analyzing your health data...</p>
                                          </div>
                                    ) : suggestionResponse ? (
                                          <div style={{ whiteSpace: 'pre-wrap', lineHeight: '1.6' }}>
                                                {suggestionResponse}
                                          </div>
                                    ) : (
                                          <div className="text-center mb-2">
                                                <p>Preparing analysis...</p>
                                          </div>
                                    )}
                              </div>
                        </CModalBody>
                        <CModalFooter>
                              {/* Audio Play/Stop Button */}
                              <CButton
                                    color={isAudioPlaying ? 'danger' : 'success'}
                                    size="sm"
                                    onClick={() => isAudioPlaying ? stopAudio() : playAudio(suggestionResponse)}
                                    disabled={isSuggestionLoading || !suggestionResponse}
                                    style={{ marginRight: 'auto' }}
                              >
                                    {isAudioPlaying ? '⏹️ Stop Audio' : '🔊 Play Audio'}
                              </CButton>
                              <CButton
                                    color="primary"
                                    size="sm"
                                    onClick={() => {
                                          stopAudio(); // Stop audio before regenerating
                                          handleSuggestionClick(selectedSuggestion);
                                    }}
                                    disabled={isSuggestionLoading}
                              >
                                    🔄 Regenerate
                              </CButton>
                              <CButton
                                    color="secondary"
                                    onClick={closeSuggestionModal}
                              >
                                    Close
                              </CButton>
                        </CModalFooter>
                  </CModal>
            </CCard>
      );
};


export default InsightsWidget;


